import { useAnalytics } from 'hooks/use-analytics/useAnalytics'
import { useLocalStorage } from 'hooks/use-local-storage/useLocalStorage'
import { useNotifications } from 'hooks/use-notifications/useNotifications'
import { useAuth } from 'pages/auth/hooks/useAuth'
import { useHistory, useLocation } from 'react-router-dom'

export const useViewNotifications = () => {
  const { backendUser, backendPartner } = useAuth()
  const { notifications } = useNotifications(backendUser?.id)
  const { logEventAnalytics } = useAnalytics()
  const history = useHistory()
  const { setItem } = useLocalStorage()
  const { pathname } = useLocation()

  const handleTracker = (notificationType: string) => {
    const partnerLog = {}
    const userLog = {}
    userLog['User ID'] = backendUser?.id
    if (backendUser?.partnerId) {
      partnerLog['Partner ID'] = backendPartner?.id
      partnerLog['Company Name'] = backendPartner?.name
      partnerLog['Account Type'] = 'Partner'
    } else {
      userLog['Account Type'] = backendUser?.role
    }
    logEventAnalytics(
      backendUser?.partnerId ? 'Partner' : backendUser?.role,
      `Notification viewed`,
      'mixpanel',
      'Notification viewed',
      {
        ...partnerLog,
        ...userLog,
        'Notification Type': notificationType
      },
      backendUser?.id,
      {
        $name: `${backendUser?.firstName} ${backendUser?.lastName}`,
        $email: backendUser?.username
      }
    )
  }

  const getDataFromMessage = (description, data) => {
    switch (data) {
      case 'message':
        try {
          return JSON.parse(description)?.message
        } catch (error) {
          return ''
        }
      case 'contentId':
        try {
          return JSON.parse(description)?.contentID
        } catch (error) {
          return ''
        }
      case 'summitId':
        try {
          return JSON.parse(description)?.summitID
        } catch (error) {
          return ''
        }
      case 'masterclassId':
        try {
          return JSON.parse(description)?.masterclassID
        } catch (error) {
          return ''
        }
      case 'followerId':
        try {
          return JSON.parse(description)?.followerID
        } catch (error) {
          return ''
        }
      case 'jobId':
        try {
          return JSON.parse(description)?.jobID
        } catch (error) {
          return ''
        }
      case 'memberId':
        try {
          return JSON.parse(description)?.memberID
        } catch (error) {
          return ''
        }
      case 'referenceId':
        try {
          return JSON.parse(description)?.referenceID
        } catch (error) {
          return ''
        }
      case 'channelId':
        try {
          return JSON.parse(description)?.channelID
        } catch (error) {
          return ''
        }
      case 'companyId':
        try {
          return JSON.parse(description)?.companyID
        } catch (error) {
          return ''
        }
      default:
        return
    }
  }

  const viewNotification = (notification) => {
    const action = notification?.action
    let notificationDescriptionId = ''
    let jobNotificationDescriptionId = ''
    let channelId = ''

    if (notification?.action.includes('summit_reminder')) {
       notificationDescriptionId = getDataFromMessage(notification?.description, 'summitId')
    } else if (notification?.action.includes('masterclass_reminder')) {
       notificationDescriptionId = getDataFromMessage(notification?.description, 'masterclassId')
    } else if (notification?.action.includes('company_followed')) {
       notificationDescriptionId = getDataFromMessage(notification?.description, 'followerId')
    } else if (notification?.action.includes('application_viewed')) {
       notificationDescriptionId = getDataFromMessage(notification?.description, 'memberId')
       jobNotificationDescriptionId = getDataFromMessage(notification?.description, 'jobId')
    } else if (notification?.action.includes('content')) {
       notificationDescriptionId = getDataFromMessage(notification?.description, 'contentId')
    } else if (notification?.action.includes('mentioned')) {
       notificationDescriptionId = getDataFromMessage(notification?.description, 'referenceId')
    } else if (notification?.action.includes('channelUser')) {
       channelId = getDataFromMessage(notification?.description, 'channelId')
    } else if (notification?.action.includes('day7IncompletePartnerProfileReminder')) {
      notificationDescriptionId = getDataFromMessage(notification?.description, 'companyId')
   }

    switch (action) {
      case 'connection_requested':
        if (pathname.includes('/connections/received')) history.go(0)
        history.push('/connections/received')
        handleTracker(action)
        return
      case 'connection_accepted':
        history.push('/connections')
        handleTracker('content_comment')
        return
      case 'content_comment':
        history.push(`/feed/post/${notificationDescriptionId}`)
        handleTracker(action)
        return
      case 'content_reaction':
        history.push(`/feed/post/${notificationDescriptionId}`)
        handleTracker(action)
        return
      case 'content_share':
        history.push(`/feed/post/${notificationDescriptionId}`)
        handleTracker(action)
        return
      case 'delete_sharedContent':
        history.push(`/feed`)
        handleTracker(action)
        return
      case 'password_changed':
        history.push(`/feed`)
        handleTracker(action)
        return
      case 'day_one_notification':
        history.push('/feed/post/create')
        handleTracker(action)
        return
      case 'day_two_notification':
        history.push('/feed/invite')
        handleTracker(action)
        return
      case 'day_three_notification':
        history.push('/events')
        handleTracker(action)
        return
      case 'day_four_notification':
        history.push('/connections')
        handleTracker(action)
        return
      case 'day_five_notification':
        history.push('/feed/post/create')
        handleTracker(action)
        return
      case 'day_seven_notification_incomplete':
        setItem('profileEdited', false)
        history.push('/profile')
        handleTracker(action)
        return
      case 'summit_ticket_purchased':
        history.push('/profile#manage-pass')
        handleTracker(action)
        return
      case 'day_seven_notification_complete':
        history.push('/profile')
        handleTracker(action)
        return
      case 'summit_reminder':
        history.push(`/summit/${notificationDescriptionId}`)
        handleTracker(action)
        return
      case 'a_week_to_summit_reminder':
        history.push(`/summit/${notificationDescriptionId}`)
        handleTracker(action)
        return
      case 'two_days_to_summit_reminder':
        history.push(`/summit/${notificationDescriptionId}`)
        handleTracker(action)
        return
      case 'a_hour_to_summit_reminder':
        history.push(`/summit/${notificationDescriptionId}`)
        handleTracker(action)
        return
      case 'twenty_four_hours_to_masterclass_reminder':
        history.push(`/event/${notificationDescriptionId}`)
        handleTracker(action)
        return
      case 'one_week_to_masterclass_reminder':
        history.push(`/event/${notificationDescriptionId}`)
        handleTracker(action)
        return
      case 'launch_day_masterclass_reminder':
        history.push(`/event/${notificationDescriptionId}`)
        handleTracker(action)
        return
      case 'create_admin_notification':
        history.push(`/company/${backendUser?.partnerId}`)
        handleTracker(action)
        return
      case 'company_followed':
       history.push(`/profile/${notificationDescriptionId}`)
        handleTracker(action)
        return
      case 'search_limit_reached':
        history.push(`/feed`)
        handleTracker(action)
        return
      case 'partner_subscription_renewal':
        history.push(`/feed`)
        handleTracker(action)
        return
      case 'day7IncompletePartnerProfileReminder':
        history.push(`/company/${notificationDescriptionId}`)
        handleTracker(action)
        return
      case 'application_viewed':
        history.push(`jobs/details/${jobNotificationDescriptionId}`)
        handleTracker(action)
        return
      case 'user_mentioned_comment':
        history.push(`/feed/post/${notificationDescriptionId}`)
        handleTracker(action)
        return
      case 'user_mentioned_post':
        history.push(`/feed/post/${notificationDescriptionId}`)
        handleTracker(action)
        return
      case 'channelUser_deleted':
        handleTracker(action)
        return
      case 'channelUser_added':
        history.push(`/channel/${channelId}`)
        handleTracker(action)
        return
      case 'partnerProfile_incomplete':
        setItem('profileEdited', false)
        history.push(`/company/${backendUser?.partnerId}`)
        handleTracker(action)
        return
      case 'partnerReminderToMakeAPost':
        const searchParams = new URLSearchParams()
        searchParams.append('createPost', 'true')
        history.push({
          pathname: '/feed', 
          search: `?${searchParams.toString()}`
        })
        handleTracker(action)
        return
      default:
        return
    }
  }
  return {
    getDataFromMessage,
    viewNotification
  } as const
}
